import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { TelephonyServiceProviderConfiguration } from '../../extViewmodels/sdk/models/Telephony';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

export const TELEPHONY_CONFIGURATION_QUERY_KEY = 'telephonyConfigurationQueryKey';

export function useTelephonyConfigurationQuery({
	refetchOnWindowFocus = false,
	retry = false,
	impersonationContext,
	enabled,
}: {
	enabled?: boolean;
	refetchOnWindowFocus?: boolean;
	retry?: boolean;
	impersonationContext?: Api.IImpersonationContext;
} = {}) {
	const userSession = useUserSession();
	return useQuery({
		queryFn: () =>
			userSession.webServiceHelper.callAsync<TelephonyServiceProviderConfiguration>({
				path: Api.ImpersonationBroker.composeApiUrl({
					urlPath: 'telephony/configuration',
					impersonationContext,
				}),
				method: 'GET',
				throwOn404: false,
			}),
		queryKey: [TELEPHONY_CONFIGURATION_QUERY_KEY, impersonationContext],
		refetchOnWindowFocus,
		retry,
		enabled,
	});
}

export const invalidateTelephonyGetConfiguration = () => {
	reactQueryClient.invalidateQueries({ queryKey: [TELEPHONY_CONFIGURATION_QUERY_KEY] });
};

// Undefined unless the refetch succeeded, so a caller never mistakes the cached configuration for a fresh one.
export const refetchTelephonyConfiguration = async (impersonationContext?: Api.IImpersonationContext) => {
	const queryKey = [TELEPHONY_CONFIGURATION_QUERY_KEY, impersonationContext];
	try {
		await reactQueryClient.invalidateQueries({ queryKey }, { throwOnError: true });
	} catch {
		return undefined;
	}
	const state = reactQueryClient.getQueryState<TelephonyServiceProviderConfiguration>(queryKey);
	return state?.isInvalidated ? undefined : state?.data;
};

export const resetTelephonyGetConfiguration = () => {
	reactQueryClient.resetQueries({ queryKey: [TELEPHONY_CONFIGURATION_QUERY_KEY] });
};
